/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/A1/components/A1Section.tsx.
 */

import React, { useEffect, useState } from "react";
import {
  ExtractionProcessOption,
  FormField,
  FormSubSection,
  type FormDataResponse,
} from "../../../../../../interfaces/formInterfaces";
import axiosClient from "../../../../../../api/axiosClient";
import { API_URLS } from "../../../../../../constants/apiUrls";
import type { FuelDensityApiRow } from "../../../../../../services/a1Service";
import type { CalorificValuePublic } from "../../../../../../services/calorificValuesPublicService";
import "./../FormA1.css";
import { A1_RAW_MATERIAL_ROW_FIELD_NAMES } from "../hooks/useA1Prefill";
import FormFieldLabel from "../../../../../../components/common/FormFieldLabel";
import { resolveContributorFieldDisplayLabel } from "../../shared/contributorFieldDisplayLabels";
import { getContributorTextInputHtmlProps } from "../../../../../../utils/contributorFormFieldInputProps";
import { formatUnitOptionLabel } from "../../../../../../utils/formatUnitSymbol";
import {
  buildContributorUnitSelectOptions,
  resolveContributorDropdownDisplayLabel,
  resolveContributorUnitDisplayLabel,
  type ContributorUnitOption,
} from "../../shared/unitSelectOptions";
import {
  filterFuelDensityUnitOptionsToKgPerM3,
  isFuelDensityUnitFormFieldName,
} from "../../shared/fuelMixReferenceAutofill";
import { resolveContributorFieldMandatory } from "../../shared/fuelMixDensityValidation";
import {
  buildEmbodiedCarbonFunctionalUnitSelectState,
  isEmbodiedCarbonFunctionalUnitFieldName,
  resolveEmbodiedCarbonFunctionalUnitDisplayLabel,
} from "../../shared/contributorPerFunctionalUnitUnits";
import {
  CF_AUTO_CALC_TITLE,
  isEmbodiedCarbonCharacterizationFactorUnitField,
  shouldDisableAutoCalculatedCharacterizationFactor,
} from "../../shared/contributorCharacterizationFactorUi";
import type { ReferenceLabels } from "../../../../../../components/submission/readonlyFormDisplay";

interface RawMat {
  id: number;
  /** Present when row was resolved to `raw_materials.id` (see useA1FormData). */
  catalogId?: number | null;
  name: string;
  qty?: string;
  unitId?: number;
}

interface Props {
  section: FormSubSection;
  rowCount: number;
  rawMaterials: RawMat[];
  /** From GET /extraction-processes?materialId= (Background finished material). */
  extractionProcesses?: ExtractionProcessOption[];
  values: Record<string, string>;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  /** Dataset Overview: all controls disabled. */
  readonly?: boolean;
  referenceLabels?: ReferenceLabels;
  errors?: Record<string, string>;
  formData?: FormDataResponse | null;
  fuelDensityCatalog?: FuelDensityApiRow[];
  calorificValuesCatalog?: CalorificValuePublic[];
  /** Background finished material unit symbol (e.g. kg) for per-functional-unit compound labels. */
  backgroundFinishedMaterialUnitSymbol?: string | null;
}

const RowSeparator = () => (
  <div className="a1-row-separator" style={{ height: 1 }} />
);

/** Chronological raw-material trip label (01, 02, …) aligned with Background Data raw material order. */
const rawMaterialTripLabel = (rowIndex: number) =>
  `Raw Material ${String(rowIndex + 1).padStart(2, "0")}`;

/** Unit row from /units or /units/property (shape varies by endpoint). */
interface UnitOption {
  id: number;
  name?: string;
  symbol?: string;
  unitSymbol?: string;
  displayName?: string;
}

const A1Section: React.FC<Props> = ({
  section,
  rowCount,
  rawMaterials,
  extractionProcesses = [],
  values,
  onChange,
  readonly = false,
  referenceLabels,
  errors = {},
  formData = null,
  fuelDensityCatalog = [],
  calorificValuesCatalog = [],
  backgroundFinishedMaterialUnitSymbol = null,
}) => {
  const code = section.code || section.name || String(section.id);
  const [unitOptionsMap, setUnitOptionsMap] = useState<Record<number, UnitOption[]>>({});
  const [allUnitsFlat, setAllUnitsFlat] = useState<UnitOption[]>([]);

  // Fetch units for all UNIT fields in this section (BY_PROPERTY(unitPropertyId))
  useEffect(() => {
    const fetchUnits = async () => {
      const unitFields = section.fields.filter((f) => f.type === "UNIT" && f.unitPropertyId);
      const promises = unitFields.map(async (field) => {
        if (!field.unitPropertyId) return;
        try {
          const res = await axiosClient.get(API_URLS.UNITS.BY_PROPERTY(field.unitPropertyId));
          if (res.data?.success && res.data?.data) {
            const units = Array.isArray(res.data.data) ? res.data.data : [];
            return { fieldId: field.id, units };
          }
        } catch (err) {
          console.warn(`Failed to fetch units for property ${field.unitPropertyId}:`, err);
        }
        return { fieldId: field.id, units: [] };
      });

      const results = await Promise.all(promises);
      const newMap: Record<number, UnitOption[]> = {};
      results.forEach((result) => {
        if (result) {
          newMap[result.fieldId] = result.units;
        }
      });
      setUnitOptionsMap(newMap);
    };

    if (section.fields.some((f) => f.type === "UNIT" && f.unitPropertyId)) {
      fetchUnits();
    }
  }, [section]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosClient.get("/units");
        const raw = res?.data?.data ?? res?.data;
        const flat: UnitOption[] = [];
        const consumeUnit = (u: {
          id?: number;
          name?: string;
          symbol?: string;
          unitSymbol?: string;
          displayName?: string;
        }) => {
          if (!u?.id) return;
          flat.push({
            id: Number(u.id),
            name: u.name || u.displayName || u.unitSymbol || u.symbol || "",
            symbol: u.symbol || u.unitSymbol,
            unitSymbol: u.unitSymbol,
            displayName: u.displayName,
          });
        };
        if (Array.isArray(raw)) {
          raw.forEach((propertyOrUnit) => {
            if (Array.isArray(propertyOrUnit?.units)) {
              propertyOrUnit.units.forEach(consumeUnit);
            } else {
              consumeUnit(propertyOrUnit);
            }
          });
        } else if (raw && Array.isArray((raw as { units?: unknown[] }).units)) {
          (raw as { units: unknown[] }).units.forEach((u) =>
            consumeUnit(u as Parameters<typeof consumeUnit>[0])
          );
        }
        if (!cancelled) {
          setAllUnitsFlat(flat.filter((u) => Number.isFinite(u.id)));
        }
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const getField = (name: string) =>
    section.fields.find((f) => f.name === name);

  /** CMS may use type EXTRACTION_PROCESS or legacy name RawMaterialExtractionProcesses. */
  const getExtractionProcessField = (): FormField | undefined =>
    section.fields.find((f) => f.type === "EXTRACTION_PROCESS") ??
    section.fields.find((f) => f.name === "RawMaterialExtractionProcesses");

  const renderInput = (f: FormField, rowIndex: number) => {
    const fieldName = `${f.name}_${code}_row${rowIndex}`;
    const cfLocked = shouldDisableAutoCalculatedCharacterizationFactor(
      section.code ?? code,
      f,
      { values, rowIndex, formData }
    );

    // Disable state and district fields (auto-populated from lat/lng)
    const forceEditable = [
      "embodiedCarbon_referenceQty_unit_extraction",
      "embodiedCarbon_functionalUnit_unit_extraction",
    ].includes(f.name);

    const isDisabled =
      readonly ||
      ((f.type === "READONLY" ||
        f.isEditable === false ||
        f.name === "extractionState" ||
        f.name === "extractionDistrict") &&
        !forceEditable);

    if (
      f.name === "extractionLatitude" ||
      f.name === "extractionLongitude"
    ) {
      return (
        <input
          type="text"
          inputMode="decimal"
          name={fieldName}
          value={values[fieldName] || ""}
          onChange={onChange}
          onBlur={onChange}
          readOnly={isDisabled}
          disabled={isDisabled}
        />
      );
    }

    if (
      cfLocked &&
      !isEmbodiedCarbonCharacterizationFactorUnitField(
        f,
        section.code ?? code,
        formData
      )
    ) {
      return (
        <input
          type="text"
          inputMode="decimal"
          name={fieldName}
          value={values[fieldName] || ""}
          readOnly
          disabled
          className="readonly-input"
          title={CF_AUTO_CALC_TITLE}
        />
      );
    }

    if (
      cfLocked &&
      isEmbodiedCarbonCharacterizationFactorUnitField(
        f,
        section.code ?? code,
        formData
      )
    ) {
      const display =
        resolveContributorUnitDisplayLabel({
          selected: String(values[fieldName] ?? ""),
          propertyUnits: unitOptionsMap[f.id] || [],
          allUnitsFlat,
          dropdownOptions: f.dropdown?.options || f.dropdownValues || [],
          referenceUnits: referenceLabels?.units,
        }) || "—";
      return (
        <input
          type="text"
          className="readonly-input"
          value={display}
          readOnly
          disabled
          title={CF_AUTO_CALC_TITLE}
        />
      );
    }

    const isRawMaterialRowField =
      f.type === "RAW_MATERIALS" ||
      A1_RAW_MATERIAL_ROW_FIELD_NAMES.has(f.name);

    if (isRawMaterialRowField) {
      const selected = values[fieldName] || "";
      const labelFromValues = values[`${fieldName}_label`] || "";
      const hasOption = rawMaterials.some(
        (rm) => String(rm.id) === String(selected)
      );

      return (
        <select
          name={fieldName}
          value={selected}
          onChange={onChange}
          disabled
        >
          <option value="">Select Raw Material...</option>
          {selected && !hasOption ? (
            <option value={String(selected)}>
              {labelFromValues || `Material (${selected})`}
            </option>
          ) : null}
          {rawMaterials.map((rm) => (
            <option key={rm.id} value={String(rm.id)}>
              {rm.name || `Material #${rm.id}`}
            </option>
          ))}
        </select>
      );
    }

    if (f.type === "EXTRACTION_PROCESS") {
      const inline = f.extractionProcesses;
      const list = (inline && inline.length > 0 ? inline : extractionProcesses)
        .slice()
        .sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
        );
      const selected = values[fieldName] || "";
      const idInList = list.some((p) => String(p.id) === String(selected));

      return (
        <select
          name={fieldName}
          value={selected}
          onChange={onChange}
          disabled={isDisabled || (list.length === 0 && !selected)}
          title={
            list.length === 0 && !selected
              ? "Choose a finished material in Background Data so extraction processes can load."
              : undefined
          }
        >
          <option value="">
            {list.length === 0
              ? "No extraction processes for this material…"
              : "Select extraction process…"}
          </option>
          {selected && !idInList ? (
            <option value={String(selected)}>
              Saved value ({selected})
            </option>
          ) : null}
          {list.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.name}
            </option>
          ))}
        </select>
      );
    }

    if (readonly && f.type === "UNIT") {
      if (isEmbodiedCarbonFunctionalUnitFieldName(f.name)) {
        const display =
          resolveEmbodiedCarbonFunctionalUnitDisplayLabel(
            backgroundFinishedMaterialUnitSymbol ?? null,
            String(values[fieldName] ?? "")
          ) || "—";
        return (
          <input
            type="text"
            className="readonly-input"
            value={display}
            readOnly
            disabled
          />
        );
      }

      const display =
        resolveContributorUnitDisplayLabel({
          selected: String(values[fieldName] ?? ""),
          propertyUnits: unitOptionsMap[f.id] || [],
          allUnitsFlat,
          dropdownOptions: f.dropdown?.options || f.dropdownValues || [],
          referenceUnits: referenceLabels?.units,
        }) || "—";
      return (
        <input
          type="text"
          className="readonly-input"
          value={display}
          readOnly
          disabled
        />
      );
    }

    if (readonly && f.type === "DROPDOWN") {
      const isExtractionProcess =
        f.name === "RawMaterialExtractionProcesses" ||
        (f.name || "").toLowerCase().includes("extractionprocess");
      const display =
        resolveContributorDropdownDisplayLabel({
          selected: String(values[fieldName] ?? ""),
          options: f.dropdown?.options || f.dropdownValues || [],
          referenceMap: isExtractionProcess
            ? referenceLabels?.extractionProcesses
            : undefined,
        }) || "—";
      return (
        <input
          type="text"
          className="readonly-input"
          value={display}
          readOnly
          disabled
        />
      );
    }

    if (f.type === "DROPDOWN") {
      return (
        <select
          name={fieldName}
          value={values[fieldName] || ""}
          onChange={onChange}
          disabled={isDisabled}
        >
          <option value="">Select...</option>
          {f.dropdown?.options?.map((opt) => (
            <option key={String(opt.value)} value={String(opt.value)}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    if (f.type === "UNIT") {
      if (isEmbodiedCarbonFunctionalUnitFieldName(f.name)) {
        const { options, selectValue } =
          buildEmbodiedCarbonFunctionalUnitSelectState(
            backgroundFinishedMaterialUnitSymbol ?? null,
            String(values[fieldName] ?? "")
          );
        return (
          <select
            name={fieldName}
            value={selectValue}
            onChange={onChange}
            disabled={isDisabled}
          >
            <option value="">Select Unit...</option>
            {options.map((opt) => (
              <option key={String(opt.value)} value={String(opt.value)}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      }

      const { options, selectValue } = buildContributorUnitSelectOptions({
        propertyUnits: unitOptionsMap[f.id] || [],
        allUnitsFlat,
        dropdownOptions: f.dropdown?.options || f.dropdownValues || [],
        selected: String(values[fieldName] ?? ""),
      });
      const unitOptions = isFuelDensityUnitFormFieldName(f.name)
        ? filterFuelDensityUnitOptionsToKgPerM3(options)
        : options;
      const unitSelectValue = unitOptions.some(
        (opt) => opt.value === selectValue
      )
        ? selectValue
        : unitOptions.length === 1
          ? unitOptions[0].value
          : "";

      return (
        <select
          name={fieldName}
          value={unitSelectValue}
          onChange={onChange}
          disabled={isDisabled}
        >
          <option value="">Select Unit...</option>
          {unitOptions.map((opt) => (
            <option key={String(opt.value)} value={String(opt.value)}>
              {opt.label ?? String(opt.value)}
            </option>
          ))}
        </select>
      );
    }

    if (f.type === "READONLY") {
      return (
        <input
          type="text"
          className="readonly-input"
          value={values[fieldName] || ""}
          readOnly
        />
      );
    }

    return (
      <input
        {...getContributorTextInputHtmlProps(f)}
        name={fieldName}
        value={values[fieldName] || ""}
        onChange={onChange}
        disabled={isDisabled}
      />
    );
  };

  const isRawMaterialRowLabelField = (field: FormField) =>
    field.type === "RAW_MATERIALS" ||
    A1_RAW_MATERIAL_ROW_FIELD_NAMES.has(field.name);

  const renderLabeled = (field?: FormField, rowIndex = 0) => {
    if (!field) return null;

    const label = isRawMaterialRowLabelField(field)
      ? rawMaterialTripLabel(rowIndex)
      : resolveContributorFieldDisplayLabel(field);
    const fieldName = `${field.name}_${code}_row${rowIndex}`;
    const fieldError = errors[fieldName];

    return (
      <div className={fieldError ? "field-has-error" : undefined}>
        <FormFieldLabel
          label={label}
          required={resolveContributorFieldMandatory({
            field,
            sectionCode: code,
            rowIndex,
            values,
            formData,
            fuelDensityCatalog,
            calorificValuesCatalog,
          })}
          infoText={field.infoText}
        />
        {renderInput(field, rowIndex)}
        {fieldError ? (
          <span className="error-message">{fieldError}</span>
        ) : null}
      </div>
    );
  };

  /* ===================== A1 RAW EXTRACTION ===================== */
  if (section.code === "A1_RAW_EXTRACTION") {
    return (
      <>
        {Array.from({ length: rowCount }).map((_, rowIndex) => (
          <React.Fragment key={rowIndex}>
            <div className="a1-raw-extraction-grid">
              <div className="field-cell">
                {renderLabeled(getField("rawMaterial_extraction"), rowIndex)}
              </div>
              <div className="field-cell">
                {renderLabeled(getField("extractionLatitude"), rowIndex)}
              </div>
              <div className="field-cell">
                {renderLabeled(getField("extractionLongitude"), rowIndex)}
              </div>
              <div className="field-cell empty-cell" />
              <div className="field-cell">
                {renderLabeled(getField("extractionState"), rowIndex)}
              </div>
              <div className="field-cell">
                {renderLabeled(getField("extractionDistrict"), rowIndex)}
              </div>
              <div className="field-cell empty-cell" />
              <div className="field-cell">
                {renderLabeled(getField("extractionPincode"), rowIndex)}
              </div>
              <div className="field-cell empty-cell" />
              <div className="field-cell empty-cell" />
              <div className="field-cell">
                {renderLabeled(getField("extractionStatus"), rowIndex)}
              </div>
              <div className="field-cell">
                {renderLabeled(getExtractionProcessField(), rowIndex)}
              </div>
            </div>

            {rowIndex < rowCount - 1 && <RowSeparator />}
          </React.Fragment>
        ))}
      </>
    );
  }

  /* ===================== A1 FUEL ===================== */
  if (section.code === "A1_FUEL_EXTRACTION") {
    return (
      <>
        {Array.from({ length: rowCount }).map((_, rowIndex) => (
          <React.Fragment key={rowIndex}>
            <div className="a1-raw-extraction-grid">
              <div className="field-cell">
                {renderLabeled(
                  getField("rawMaterial_extraction_details"),
                  rowIndex
                )}
              </div>
              <div className="field-cell">
                {renderLabeled(getField("fuelMix_extraction"), rowIndex)}
              </div>
              <div className="field-cell empty-cell" />
              <div className="field-cell empty-cell" />
              <div className="field-cell">
                {renderLabeled(getField("fuelQuantity_extraction"), rowIndex)}
              </div>
              <div className="field-cell">
                {renderLabeled(
                  getField("fuelQuantity_unit_extraction"),
                  rowIndex
                )}
              </div>
              <div className="field-cell empty-cell" />
              <div className="field-cell">
                {renderLabeled(getField("fuelDensity_extraction"), rowIndex)}
              </div>
              <div className="field-cell">
                {renderLabeled(
                  getField("fuelDensity_unit_extraction"),
                  rowIndex
                )}
              </div>
              <div className="field-cell empty-cell" />
              <div className="field-cell">
                {renderLabeled(
                  getField("calorificValue_extraction"),
                  rowIndex
                )}
              </div>
              <div className="field-cell">
                {renderLabeled(
                  getField("calorificValue_unit_extraction"),
                  rowIndex
                )}
              </div>
            </div>

            {rowIndex < rowCount - 1 && <RowSeparator />}
          </React.Fragment>
        ))}
      </>
    );
  }

  /* ===================== A1 ENERGY ===================== */
  if (section.code === "A1_EMBODIED_ENERGY") {
    return (
      <>
        {Array.from({ length: rowCount }).map((_, rowIndex) => (
          <React.Fragment key={rowIndex}>
            <div className="a1-raw-extraction-grid">
              <div className="field-cell">
                {renderLabeled(
                  getField("rawMaterialsExtractionProcesses"),
                  rowIndex
                )}
              </div>
              <div className="field-cell">
                {renderLabeled(
                  getField("embodiedEnergy_extraction"),
                  rowIndex
                )}
              </div>
              <div className="field-cell">
                {renderLabeled(
                  getField("embodiedEnergy_unit_extraction"),
                  rowIndex
                )}
              </div>
            </div>

            {rowIndex < rowCount - 1 && <RowSeparator />}
          </React.Fragment>
        ))}
      </>
    );
  }

  /* ===================== A1 CARBON ===================== */
  if (section.code === "A1_EMBODIED_CARBON") {
    return (
      <>
        {Array.from({ length: rowCount }).map((_, rowIndex) => (
          <React.Fragment key={rowIndex}>
            <div className="a1-raw-extraction-grid">
              <div className="field-cell">
                {renderLabeled(
                  getField("rawMaterialsEmbodiedCarbonDetails"),
                  rowIndex
                )}
              </div>
              <div className="field-cell">
                {renderLabeled(
                  getField("characterizationFactor_extraction"),
                  rowIndex
                )}
              </div>
              <div className="field-cell">
                {renderLabeled(
                  getField("characterizationFactor_unit_extraction"),
                  rowIndex
                )}
              </div>
              <div className="field-cell empty-cell" />
              <div className="field-cell">
                {renderLabeled(
                  getField("embodiedCarbon_referenceQty_extraction"),
                  rowIndex
                )}
              </div>
              <div className="field-cell">
                {renderLabeled(
                  getField(
                    "embodiedCarbon_referenceQty_unit_extraction"
                  ),
                  rowIndex
                )}
              </div>
              <div className="field-cell empty-cell" />
              <div className="field-cell">
                {renderLabeled(
                  getField(
                    "embodiedCarbon_functionalUnit_extraction"
                  ),
                  rowIndex
                )}
              </div>
              <div className="field-cell">
                {renderLabeled(
                  getField(
                    "embodiedCarbon_functionalUnit_unit_extraction"
                  ),
                  rowIndex
                )}
              </div>
            </div>

            {rowIndex < rowCount - 1 && <RowSeparator />}
          </React.Fragment>
        ))}
      </>
    );
  }

  return null;
};

/** React component — A1Section. */
export default A1Section;